import { useState, useMemo } from 'react';

type InitialState = string | number | boolean | Record<string, unknown>;
type State<T extends InitialState> = T | false;

type ShowState<T extends InitialState> = Exclude<T, false>;
type HideState = false;

type Action = (props?: unknown) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CallbackProps<T = any> = T;
interface CallbackOptions<T extends InitialState> {
  onShow?: (props: CallbackProps, previousState: HideState) => ShowState<T> | void;
  onHide?: (props: CallbackProps, previousState: ShowState<T>) => HideState | void;
}

interface Handler {
  show: Action;
  hide: Action;
  toggle: Action;
}

/**
 * Manages boolean states, can be wired to callbacks on state change for easier readability actions associated with state.
 *
 * @example
 * const [open, handler] = useDisclosure(false, { onShow: () => console.log("foo") });
 * handler.toggle();
 */
export const useDisclosure = <T extends InitialState = boolean>(
  initialState = false,
  callbacks?: CallbackOptions<T>,
): [State<T>, Handler] => {
  const [state, setState] = useState<State<T>>(initialState as State<T>);

  const onShow = callbacks?.onShow;
  const onHide = callbacks?.onHide;

  const handler = useMemo<Handler>(
    () => ({
      show: (props: unknown) =>
        setState((previousState) => {
          if (previousState === false)
            return onShow?.(props, previousState) ?? (true as ShowState<T>);
          return previousState;
        }),
      hide: (props: unknown) =>
        setState((previousState) => {
          if (previousState !== false)
            return onHide?.(props, previousState as ShowState<T>) ?? false;
          return previousState;
        }),
      toggle: (props: unknown) =>
        setState((previousState) => {
          if (previousState === false && props !== false)
            return onShow?.(props, previousState) ?? (true as ShowState<T>);
          return onHide?.(props, previousState as ShowState<T>) ?? false;
        }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [state, handler];
};
